// ... existing imports ...
import { formatPercent, formatThousands, isNullOrUndefined } from '.';

interface ROIValues {
  projectedReturnOnInvestment: number;
  projectedCompoundAnnualGrowthRate: number;
  projectedInternalRateOfReturn: number | undefined;
  investmentPeriodYears: number;
  isIrrSuspect?: boolean;
  // A saved quote priced for client details that no longer hold (see
  // `outdatedSavedQuote.ts`). Its ROI is meaningless, so none is shown.
  quoteOutdatedReason?: string;
}

export const IRR_SUSPECT_MESSAGE = 'Not calculable';
export const IRR_SUSPECT_EXPLANATION =
  'IRR cannot be reliably determined for products where payments overlap with benefit payouts. Use Total ROI or Annual ROI instead.';
// A saved quote that no longer fits the client is labelled "Quote invalid" in
// two places: on its badge, and in the slot where its ROI would be. What changed
// and what to do about it follow in the tooltip or, in the reports, a note.
export const QUOTE_INVALID_MESSAGE = 'Quote invalid';
export const QUOTE_OUTDATED_EXPLANATION =
  'This policy is not counted in the plan totals. Get a new quote or remove this policy.';
export const IRR_SUSPECT_EXPLANATION_COMBINED =
  "A combined IRR can't be reliably calculated for this client. When multiple funding sources are added together, payments from one source can overlap with payouts from another, and the combined cash flow timing has more than one valid mathematical answer. Refer to each funding source's individual IRR above, or use Total ROI or CAGR instead.";

export function formatROI(
  values: ROIValues,
  calculationType: ReturnOnInvestmentCalculationType | undefined,
): string {
  const formatPercentToTwoDecimals = (value: number) => formatPercent(value, 2);
  const formatYears = (years: number) =>
    `${formatThousands(years, '0', 1)} ${years === 1 ? 'yr' : 'yrs'}`;

  if (values.quoteOutdatedReason) return QUOTE_INVALID_MESSAGE;

  switch (calculationType) {
    case 'compoundAnnualGrowthRate':
      if (values.investmentPeriodYears <= 0) return 'N/A';
      if (!isFinite(values.projectedCompoundAnnualGrowthRate)) return 'N/A';
      return `${formatPercentToTwoDecimals(values.projectedCompoundAnnualGrowthRate)} (${formatYears(values.investmentPeriodYears)})`;
    case 'internalRateOfReturn':
      if (isNullOrUndefined(values.projectedInternalRateOfReturn) || values.isIrrSuspect) {
        return IRR_SUSPECT_MESSAGE;
      }
      if (!isFinite(values.projectedInternalRateOfReturn)) return 'N/A';
      return `${formatPercentToTwoDecimals(values.projectedInternalRateOfReturn)} (${formatYears(values.investmentPeriodYears)})`;
    case 'total':
    default:
      if (!isFinite(values.projectedReturnOnInvestment)) return 'N/A';
      return formatPercentToTwoDecimals(values.projectedReturnOnInvestment);
  }
}

/**
 * The note reports print after the "Quote invalid" label, in place of the web
 * tooltip: what changed, then what to do.
 */
export function formatQuoteOutdatedNote(quoteOutdatedReason: string): string {
  return `${quoteOutdatedReason} ${QUOTE_OUTDATED_EXPLANATION}`;
}

export function getROILabel(
  calculationType: ReturnOnInvestmentCalculationType | undefined,
): string {
  switch (calculationType) {
    case 'internalRateOfReturn':
      return 'Annual IRR';
    case 'compoundAnnualGrowthRate':
      return 'Annual ROI';
    case 'total':
    default:
      return 'Total ROI';
  }
}
